import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { ApiHttp, SCHEMA_VERSION } from '@core/api/api-http';
import { flattenCollection } from '@features/opportunities/services/contract';
import { TagEntityType, TagService } from '@features/opportunities/services/tag.service';
import { Tag } from '@shared/models/tag';
import { Task } from '@features/tasks/models/task';
import {
  SearchOptions,
  TaskService
} from '@features/tasks/services/task.service';

import { ContactEventService } from './contact-event.service';
import { EntityType } from '../models';
import { environment } from '@env/environment';

@Injectable()
export class ContactTaskService extends TaskService {
  constructor(
    private http: ApiHttp,
    private contactEventService: ContactEventService,
    private tagService: TagService
  ) {
    super();
  }

  entityId$ = this.contactEventService.selectedContactInfo$.pipe(
    map((c) => c.id)
  );
  tagEntityType$: Observable<TagEntityType> = this.contactEventService.selectedContactInfo$.pipe(
    map((c) => this.toTagEntityType(c.entityType))
  );
  private entityTypePath$ = this.contactEventService.selectedContactInfo$.pipe(
    map((c) => this.getEntityPath(c.entityType))
  );

  getTasks(entityId: string, options: SearchOptions): Observable<Task.Model[]> {
    let params: any = {};
    if (options.searchString) {
      params.Search = options.searchString;
    }

    let queryParams = Object.keys(params) ? params : null;

    let httpOptions = {
      headers: ApiHttp.getStandardOptions().headers,
      queryParams
    };

    return this.entityTypePath$.pipe(
      switchMap(([p, entitySchemaVersion]) =>
        this.http
          .getEntity(
            environment.clientsApiUrl,
            `${p}/${entityId}/tasks`,
            entitySchemaVersion,
            httpOptions
          )
          .pipe(
            map((x) =>
              flattenCollection<Task.Model>(x).map((x: Task.Model) => x)
            )
          )
      )
    );
  }

  getTaskDefinition(): Observable<Task.Definition> {
    return this.entityTypePath$.pipe(
      take(1),
      switchMap(([p, entitySchemaVersion]) =>
        this.http.getEntity<Task.ClientSchemaDefinition>(
          environment.clientsApiUrl,
          `${p}/schema/tasks`,
          entitySchemaVersion,
          ApiHttp.getStandardOptions()
        )
      ),
      map(Task.mapClientSchemaToDefinition)
    );
  }

  getTaskForEdit(
    opportunityId: string,
    taskId: string
  ): Observable<Task.Model> {
    return this.entityTypePath$.pipe(
      take(1),
      switchMap(([p, entitySchemaVersion]) =>
        this.http.getEntity<Task.Model>(
          environment.clientsApiUrl,
          `${p}/${opportunityId}/tasks/${taskId}`,
          entitySchemaVersion,
          ApiHttp.getStandardOptions()
        )
      )
    );
  }

  updateTask(
    opportunityId: string,
    taskId: string,
    change: any
  ): Observable<any> {
    return this.entityTypePath$.pipe(
      switchMap(([p, entitySchemaVersion]) =>
        this.http.put(
          environment.clientsApiUrl,
          `${p}/${opportunityId}/tasks/${taskId}`,
          change,
          entitySchemaVersion,
          ApiHttp.getStandardOptions()
        )
      )
    );
  }

  completeTask(opportunityId: string, taskId: string): Observable<any> {
    return this.entityTypePath$.pipe(
      switchMap(([p, entitySchemaVersion]) =>
        this.http.postEntity(
          environment.clientsApiUrl,
          `${p}/${opportunityId}/tasks/${taskId}/complete`,
          null,
          entitySchemaVersion,
          ApiHttp.getStandardOptions()
        )
      )
    );
  }

  deleteTask(opportunityId: string, taskId: string): Observable<any> {
    return this.entityTypePath$.pipe(
      switchMap(([p, entitySchemaVersion]) =>
        this.http.deleteEntity(
          environment.clientsApiUrl,
          `${p}/${opportunityId}/tasks/${taskId}`,
          entitySchemaVersion
        )
      )
    );
  }

  markTaskAsReviewed(entityId: string, taskId: string): Observable<any> {
    return this.entityTypePath$.pipe(
      switchMap(([p, entitySchemaVersion]) =>
        this.http.postEntity(
          environment.clientsApiUrl,
          `${p}/${entityId}/tasks/${taskId}/reviewed`,
          null,
          entitySchemaVersion,
          ApiHttp.getStandardOptions()
        )
      )
    );
  }

  private getEntityPath(entityType: EntityType): [string, number] {
    switch (entityType) {
      case EntityType.Company:
        return ['companies', SCHEMA_VERSION.COMPANY];
      case EntityType.Individual:
        return ['individuals', SCHEMA_VERSION.INDIVIDUAL];
      case EntityType.Trust:
        return ['trusts', SCHEMA_VERSION.TRUST];
    }
  }

  private toTagEntityType(entityType: EntityType): TagEntityType {
    switch (entityType) {
      case EntityType.Individual:
        return TagEntityType.Individuals;
      case EntityType.Company:
        return TagEntityType.Companies;
      case EntityType.Trust:
        return TagEntityType.Trusts;
    }
  }

  getTaskTags(entityId: string, taskId: string): Observable<Tag[]> {
    return this.tagEntityType$.pipe(
      take(1),
      switchMap((type) => this.tagService.getTaskTags(type, entityId, taskId))
    );
  }
}
